import { STANDARD_ALLOWED_ENTITY_TYPES } from '@unifyapps/carbon/no-code/utils/interfaceQueryFilters';
import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import {
  useGetEntitySnapshot,
  useSearchInterfacePageEmbeddedEntities,
} from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import { useMemo } from 'react';
import { useSavedInterfaceVersionDetails } from '../../../../context/SavedInterfaceVersionDetailsProvider';
import {
  getAllPagesFromEmbedded,
  normalizeEmbeddedDependents,
} from '../../../../utils/normalizeEmbeddedDependents';
import { useFetchInterfacePageNamespacesForPages } from '../../standardEntities/data/useFetchInterfaceNamespaces/useFetchInterfacePageNamespacesForPages';
import type { UseFetchResourcesHookType } from '../../types';
import { useFetchCustomBlockSnapshotsForPages } from '../../useFetchCustomBlocks/useFetchCustomBlockSnapshotsForPages';
import { useInterfacePageResourcesFromSnapshot } from './useInterfacePageResourcesFromSnapshot';

const useFetchSavedInterfaceResourcesByVersion: UseFetchResourcesHookType = ({
  interfaceId,
  interfaceComponent,
  mode,
  locale,
}) => {
  const { currentVersion, componentVersion } = useSavedInterfaceVersionDetails();

  // A page that's embedded inside another saved-version page can reuse the parent's
  // already-fetched snapshot instead of firing its own network calls. Context carries
  // that parent snapshot when present; absence means we're the top-level page and own
  // the fetches.
  const {
    interfaceResourcesSnapshot,
    hasInterfaceResourcesSnapshot,
    interfacePage: interfacePageFromSnapshot,
    interfaceRecord: interfaceRecordFromSnapshot,
  } = useInterfacePageResourcesFromSnapshot({
    interfaceId,
    interfacePageId: interfaceComponent.id,
  });
  const isTopLevelPage = !hasInterfaceResourcesSnapshot;

  const {
    data: networkInterfaceRecord,
    isLoading: isLoadingInterfaceRecord,
    error: errorInterfaceRecord,
  } = useGetEntitySnapshot<InterfaceEntity | undefined>(
    UAEntityTypes.Interface,
    interfaceId,
    { version: currentVersion },
    { query: { enabled: isTopLevelPage } },
  );

  // One server-side deep traversal per top-level preview: returns this page plus every
  // embedded module, interface record, and datasource at their correct historical versions
  // in a single response. Nested calls skip this entirely and read from the parent snapshot.
  const {
    data: deepTraversalResponse,
    isLoading: isLoadingDeepTraversal,
    error: errorDeepTraversal,
  } = useSearchInterfacePageEmbeddedEntities(
    {
      entityId: interfaceComponent.id ?? '',
      version: componentVersion,
      allowedEntityTypes: STANDARD_ALLOWED_ENTITY_TYPES,
    },
    {
      query: {
        enabled: isTopLevelPage && Boolean(interfaceComponent.id) && componentVersion !== undefined,
        retry: 3,
      },
    },
  );

  const networkSnapshot = useMemo(
    () => normalizeEmbeddedDependents(deepTraversalResponse),
    [deepTraversalResponse],
  );

  // Unify both sources into the same shape so the rest of the hook is branch-free.
  // Cross-interface modules absent from the parent snapshot resolve to undefined here;
  // downstream consumers treat that as "render nothing" rather than fetching their own
  // historical state (which isn't recoverable — the parent's traversal never visited the
  // foreign interface).
  const snapshot = isTopLevelPage
    ? {
        interfaceRecord: networkInterfaceRecord,
        interfacePage: networkSnapshot.interfacePage,
        dataSources: networkSnapshot.dataSources,
        embeddedPages: networkSnapshot.embeddedPages,
        embeddedInterfaceRecords: networkSnapshot.embeddedInterfaceRecords,
      }
    : {
        interfaceRecord: interfaceRecordFromSnapshot,
        interfacePage: interfacePageFromSnapshot,
        dataSources: interfaceResourcesSnapshot?.dataSources ?? {},
        embeddedPages: interfaceResourcesSnapshot?.interfacePages ?? {},
        embeddedInterfaceRecords: interfaceResourcesSnapshot?.interfaceRecords ?? {},
      };

  // Partial entity records (lacking `.properties`) are unusable — drop them so downstream
  // `.properties` reads short-circuit on undefined instead of throwing.
  const interfaceRecord = snapshot.interfaceRecord?.properties
    ? snapshot.interfaceRecord
    : undefined;
  const { interfacePage, dataSources, embeddedPages, embeddedInterfaceRecords } = snapshot;

  const allPages = useMemo(
    () => getAllPagesFromEmbedded(interfacePage, embeddedPages),
    [interfacePage, embeddedPages],
  );

  const {
    namespaces,
    isLoading: isLoadingNamespaces,
    error: errorNamespaces,
  } = useFetchInterfacePageNamespacesForPages({
    interfaceId,
    pages: allPages,
    interfaceRecord,
    mode,
    locale,
  });

  const interfacePageNamespace = interfacePage?.id ? namespaces?.[interfacePage.id] : undefined;

  const { data: customBlocksSnapshots, isLoading: isLoadingCustomBlocksSnapshots } =
    useFetchCustomBlockSnapshotsForPages(allPages);

  // Top-level owns the deep-traversal calls; nested reads from the parent snapshot synchronously,
  // so its loading/error state only reflects the namespace fetch (the one call nested still makes).
  const isLoading =
    (isTopLevelPage && (isLoadingInterfaceRecord || isLoadingDeepTraversal)) ||
    isLoadingNamespaces ||
    isLoadingCustomBlocksSnapshots;

  const error = isTopLevelPage
    ? errorInterfaceRecord || errorDeepTraversal || errorNamespaces
    : errorNamespaces;

  return {
    isLoading,
    error,
    data: interfaceRecord,
    dataSources,
    interfacePage,
    interfacePageNamespace,
    namespaces,
    embeddedPages,
    embeddedInterfaceRecords,
    customBlocksSnapshots,
  };
};

export default useFetchSavedInterfaceResourcesByVersion;
